import { baseUrl } from './api.js';

/**
 * Krok LV1 — `/v1/cases/:id/lv-purchase` (podklad pro tlacitko + nakup).
 *
 * Rucni `fetch` (ne `apiClient`) ze STEJNEHO duvodu jako `price-range-api.ts`:
 * generovany klient uz trasu zna, ale kazdy dalsi `apiClient.GET` ve velkem
 * modulu prelezl strop hloubky typu (`TS2589: Type instantiation is
 * excessively deep`) a shodil typecheck celeho web-adminu. Rucni fetch je
 * tu proto ZMERENA odlisnost, ne lenost.
 *
 * VLASTNI MODUL (ne `cases-api.ts`): tam uz strop padal, a nova funkce by
 * navic shodila cizi `vi.mock('../lib/cases-api.js')` v sousednich sadach
 * (chybejici klic v rucni atrape je TVRDA chyba, ne `undefined`).
 */

/**
 * `available: false` = tlacitko se NEKRESLI. Rozhoduje SERVER
 * (`lvPurchaseDecision`: priznak `LV_PURCHASE_ENABLED`, souhlas ČÚZK se
 * sirenim nebo vyjimka KNT1, prostredi WSDP) — obrazovka si zadny priznak
 * necte, aby nemohla slibit funkci, kterou by routa odmitla.
 */
export interface LvAvailability {
  readonly available: boolean;
  /** Cena jednoho vypisu v Kc BEZ DPH; `null` pri nedostupnosti. */
  readonly priceCzk: number | null;
  /** Zustatek kreditu kancelare v Kc; `null` pri nedostupnosti. */
  readonly balanceCzk: string | null;
  /** `Zkušební data ČÚZK` u zkusebniho prostredi, jinak `null`. */
  readonly trialLabel: string | null;
  /**
   * Den platnosti UZ ZAPLACENEHO vypisu (`YYYY-MM-DD`), nebo `null`.
   * Katastr se meni po dnech, takze to NENI „je to aktualni" — je to
   * „tohle uz mame a je z tohoto dne".
   */
  readonly lastPurchasedOn: string | null;
  /** `true` = dalsi stazeni je dnes zdarma (tyz LV, tyz den). Pocita SERVER. */
  readonly freeToday: boolean;
}

export interface LvPurchaseOutcome {
  readonly status: 'FETCHED' | 'CACHED' | 'BUDGET_EXCEEDED';
  /** Kolik se kancelari strhlo z kreditu (0 u zasahu cache). */
  readonly chargedCzk: number;
  readonly lvNumber: string | null;
  readonly factKeys: readonly string[];
  readonly prefilledFields: readonly {
    readonly factKey: string;
    readonly contractKey: string | null;
  }[];
  /** Rozpory urad vs. potvrzeny zapis maklere — UI je ukaze jako upozorneni. */
  readonly conflicts: readonly {
    readonly factKey: string;
    readonly fromLv: unknown;
    readonly fromUser: unknown;
  }[];
  readonly trialLabel: string | null;
}

/** Strojovy kod chyby z `problem+json` — UI podle nej vybira hlasku. */
export class LvPurchaseError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'LvPurchaseError';
  }
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { title?: string; detail?: string; type?: string }
      | null;
    throw new LvPurchaseError(
      res.status,
      body?.type ?? null,
      body?.detail ?? body?.title ?? `HTTP ${String(res.status)}`,
    );
  }
  return (await res.json()) as T;
}

export async function getLvAvailability(caseId: string): Promise<LvAvailability> {
  const res = await fetch(`${baseUrl}/v1/cases/${encodeURIComponent(caseId)}/lv-purchase`, {
    credentials: 'include',
  });
  return asJson<LvAvailability>(res);
}

/**
 * Stahne vypis z LV za kredit kancelare. SYNCHRONNE (na rozdil od
 * predvyplneni ze ZAK1): makler potvrdil VYDAJ a musi se hned dozvedet,
 * kolik se strhlo a jestli se udaje rozchazi s tim, co zapsal.
 */
export async function purchaseLv(caseId: string): Promise<LvPurchaseOutcome> {
  /*
   * ZADNE TELO: predmet (k. u. + parcela) urci server ze zadani zakazky.
   * Kdyby ho posilala obrazovka, slo by zmenou cisla zaplatit z kreditu
   * kancelare za cizi pozemek a dostat k nemu vlastnika.
   */
  const res = await fetch(`${baseUrl}/v1/cases/${encodeURIComponent(caseId)}/lv-purchase`, {
    method: 'POST',
    credentials: 'include',
  });
  return asJson<LvPurchaseOutcome>(res);
}
